import { Divider, Grid, Switch, Typography } from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import {
  getUserNotificationSettings,
  updateUserNotificationSetting as updateUserNotificationSettingMutation,
  UserNotificationSetting,
} from 'vendor-marketplace-shared-library';
import useAlert from '../../../hooks/useAlert';
import { UPDATE_USER_NOTIFICATION_SETTINGS } from '../../../error-messages';
import { useState } from 'react';

export default function NotificationSettingsTab() {
  const { setAlert } = useAlert();

  const [loadingIds, setLoadingIds] = useState<string[]>([]);

  const { data } = useQuery(getUserNotificationSettings);
  const [updateUserNotificationSetting] = useMutation(updateUserNotificationSettingMutation, {
    refetchQueries: [getUserNotificationSettings],
    onError: () => {
      setAlert({ severity: 'error', message: UPDATE_USER_NOTIFICATION_SETTINGS });
    },
  });

  const onUpdateUserNotificationSetting = async (
    notificationTypeId: string,
    method: 'push' | 'text' | 'email',
    isEnabled: boolean,
  ) => {
    setLoadingIds(prev => [...prev, `${notificationTypeId}:${method}`]);
    await updateUserNotificationSetting({
      variables: {
        notificationTypeId,
        input: {
          [`${method}Enabled`]: isEnabled,
        },
      },
    });
    setTimeout(() => {
      setLoadingIds(prev => prev.filter(id => id !== `${notificationTypeId}:${method}`));
    }, 50);
  };

  const groupUserNotificationSettingsByNotificationCategoryId = (): [number[], any] => {
    const orderedUserNotificationSettingKeys: number[] = [];
    const userNotificationSettings: any = {};
    const sortedUserNotificationSettings = [...(data?.getUserNotificationSettings ?? [])].sort(
      (a: UserNotificationSetting, b: UserNotificationSetting) => {
        return (
          a.notificationType!.notificationCategory!.priority! -
          b.notificationType!.notificationCategory!.priority!
        );
      },
    );
    for (const setting of sortedUserNotificationSettings) {
      if (!userNotificationSettings[setting.notificationType!.notificationCategory!.id]?.length) {
        orderedUserNotificationSettingKeys.push(
          Number(setting.notificationType!.notificationCategory!.id),
        );
        userNotificationSettings[setting.notificationType!.notificationCategory!.id] = [setting];
      } else {
        userNotificationSettings[setting.notificationType!.notificationCategory!.id].push(setting);
      }
    }

    // For each notification category (key)
    for (const key in userNotificationSettings) {
      // Sort each group of userNotificationSettings (array) by notificationTypeId
      userNotificationSettings[key].sort(
        (a: UserNotificationSetting, b: UserNotificationSetting) => {
          return Number(a.notificationTypeId) - Number(b.notificationTypeId);
        },
      );
    }
    return [orderedUserNotificationSettingKeys, userNotificationSettings];
  };

  const [orderedUserNotificationSettingKeys, userNotificationSettings] =
    groupUserNotificationSettingsByNotificationCategoryId();

  return (
    <Grid container padding={2}>
      {orderedUserNotificationSettingKeys.map((key: number, i: number) => {
        if (!userNotificationSettings[key]?.length) return null;
        return (
          <Grid container key={key} spacing={2}>
            <Grid item xs={12}>
              <Typography variant={'h6'}>
                {userNotificationSettings[key][0]?.notificationType?.notificationCategory?.name}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              {userNotificationSettings[key].map(
                (setting: UserNotificationSetting, index: number) => {
                  if (!setting.notificationType?.id) return null;
                  return (
                    <Grid
                      item
                      container
                      key={setting.notificationTypeId}
                      sx={[
                        { backgroundColor: index % 2 ? '#f9f9f9' : 'white' },
                        styles.nameContainer,
                      ]}
                    >
                      {/*Name and Description*/}
                      <Grid item>
                        <Grid container direction={'column'}>
                          <Grid item>
                            <Typography>{setting.notificationType.name}</Typography>
                          </Grid>
                          <Grid item>
                            <Typography variant={'caption'}>
                              {setting.notificationType.description}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>

                      {/*toggles*/}
                      <Grid item>
                        <Grid container>
                          <Grid item>
                            <Typography>Push</Typography>
                            {loadingIds.includes(`${setting.notificationType.id}:push`) ? (
                              <Typography>Loading...</Typography>
                            ) : (
                              <Switch
                                checked={setting.pushEnabled!}
                                onChange={() => {
                                  if (setting.notificationType) {
                                    onUpdateUserNotificationSetting(
                                      setting.notificationType.id,
                                      'push',
                                      !setting.pushEnabled,
                                    );
                                  }
                                }}
                              />
                            )}
                          </Grid>
                          <Grid item>
                            <Typography>SMS</Typography>
                            {loadingIds.includes(`${setting.notificationType.id}:text`) ? (
                              <Typography>Loading...</Typography>
                            ) : (
                              <Switch
                                checked={setting.textEnabled!}
                                onChange={() => {
                                  if (setting.notificationType) {
                                    onUpdateUserNotificationSetting(
                                      setting.notificationType.id,
                                      'text',
                                      !setting.textEnabled,
                                    );
                                  }
                                }}
                              />
                            )}
                          </Grid>
                          <Grid item>
                            <Typography>Email</Typography>
                            {loadingIds.includes(`${setting.notificationType.id}:email`) ? (
                              <Typography>Loading...</Typography>
                            ) : (
                              <Switch
                                checked={setting.emailEnabled!}
                                onChange={() => {
                                  if (setting.notificationType) {
                                    onUpdateUserNotificationSetting(
                                      setting.notificationType.id,
                                      'email',
                                      !setting.emailEnabled,
                                    );
                                  }
                                }}
                              />
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  );
                },
              )}
            </Grid>
            <Grid item xs={12} paddingY={2}>
              <Divider />
            </Grid>
          </Grid>
        );
      })}
    </Grid>
  );
}

const styles = {
  nameContainer: {
    justifyContent: 'space-between',
    borderRadius: 2,
    padding: 1,
  },
};
